import * as THREE from 'three';

export const MULTI_SELECTION_PIVOT_NAME = '__MultiSelectionPivot__';

const _box = new THREE.Box3();
const _center = new THREE.Vector3();
const _corner = new THREE.Vector3();
const _startPivotWorld = new THREE.Matrix4();
const _deltaMatrix = new THREE.Matrix4();
const _newWorld = new THREE.Matrix4();
const _parentInv = new THREE.Matrix4();
const _objStartWorld = new Map();

let activePivot = null;
let mouseDownHandler = null;
let objectChangeHandler = null;
let sceneRef = null;
let transformControlsRef = null;

export function isMultiSelectionPivot(object) {
  return object?.userData?.isMultiSelectionPivot === true;
}

export function setMultiSelectionContext({ scene, transformControls }) {
  if (scene) sceneRef = scene;
  if (transformControls) transformControlsRef = transformControls;
}

function removePivotFromScene() {
  const scene = sceneRef;
  if (!scene) return;
  const existing =
    activePivot || scene.getObjectByName(MULTI_SELECTION_PIVOT_NAME);
  if (existing?.parent) {
    existing.parent.remove(existing);
  }
  activePivot = null;
}

function snapshotSelection(objects) {
  _objStartWorld.clear();
  objects.forEach((obj) => {
    if (!obj) return;
    obj.updateMatrixWorld(true);
    _objStartWorld.set(obj.uuid, obj.matrixWorld.clone());
  });
}

function applyPivotDelta(objects) {
  if (!activePivot || !objects?.length) return;

  _deltaMatrix
    .copy(activePivot.matrixWorld)
    .multiply(
      _startPivotWorld
        .copy(activePivot.userData.dragStartPivotWorld)
        .invert()
    );

  objects.forEach((obj) => {
    const startWorld = _objStartWorld.get(obj.uuid);
    if (!startWorld || !obj.parent) return;

    _newWorld.copy(_deltaMatrix).multiply(startWorld);
    _parentInv.copy(obj.parent.matrixWorld).invert();
    obj.matrix.copy(_parentInv).multiply(_newWorld);
    obj.matrix.decompose(obj.position, obj.quaternion, obj.scale);
    obj.updateMatrixWorld(true);
  });
}

const _sizeCheck = new THREE.Vector3();

function computePivotPosition(objects, target) {
  _box.makeEmpty();
  for (const obj of objects) {
    if (!obj) continue;
    obj.updateMatrixWorld(true);
    const sizeBefore = _box.getSize(_sizeCheck).lengthSq();
    _box.expandByObject(obj);
    const sizeAfter = _box.getSize(_sizeCheck).lengthSq();
    // Lights/cameras with no geometry still contribute their world position.
    if (sizeAfter <= sizeBefore + 1e-12) {
      _corner.setFromMatrixPosition(obj.matrixWorld);
      _box.expandByPoint(_corner);
    }
  }
  if (_box.isEmpty()) return false;
  _box.getCenter(target);
  return true;
}

function bindTransformListeners(transformControls, objects) {
  if (mouseDownHandler) {
    transformControls.removeEventListener('mouseDown', mouseDownHandler);
  }
  if (objectChangeHandler) {
    transformControls.removeEventListener('objectChange', objectChangeHandler);
  }

  mouseDownHandler = () => {
    snapshotSelection(objects);
    if (activePivot) {
      activePivot.updateMatrixWorld(true);
      activePivot.userData.dragStartPivotWorld =
        activePivot.matrixWorld.clone();
    }
  };

  objectChangeHandler = () => {
    if (transformControls.object !== activePivot) return;
    applyPivotDelta(objects);
  };

  transformControls.addEventListener('mouseDown', mouseDownHandler);
  transformControls.addEventListener('objectChange', objectChangeHandler);
}

function unbindTransformListeners(transformControls) {
  if (!transformControls) return;
  if (mouseDownHandler) {
    transformControls.removeEventListener('mouseDown', mouseDownHandler);
  }
  if (objectChangeHandler) {
    transformControls.removeEventListener('objectChange', objectChangeHandler);
  }
  mouseDownHandler = null;
  objectChangeHandler = null;
}

export function attachMultiSelectionTransform({
  scene,
  transformControls,
  objects,
} = {}) {
  const resolvedScene = scene || sceneRef;
  const resolvedControls = transformControls || transformControlsRef;

  detachMultiSelectionTransform({
    scene: resolvedScene,
    transformControls: resolvedControls,
  });

  const list = (objects || []).filter(Boolean);
  if (list.length === 0) return null;

  if (list.length === 1) {
    resolvedControls?.attach?.(list[0]);
    return list[0];
  }

  if (!resolvedScene || !resolvedControls) return null;

  setMultiSelectionContext({
    scene: resolvedScene,
    transformControls: resolvedControls,
  });

  activePivot = new THREE.Object3D();
  activePivot.name = MULTI_SELECTION_PIVOT_NAME;
  activePivot.userData.isMultiSelectionPivot = true;
  activePivot.userData.multiSelectionObjects = list;

  if (!computePivotPosition(list, _center)) {
    activePivot = null;
    return null;
  }

  activePivot.position.copy(_center);
  activePivot.quaternion.identity();
  activePivot.scale.set(1, 1, 1);
  resolvedScene.add(activePivot);
  activePivot.updateMatrixWorld(true);
  activePivot.userData.dragStartPivotWorld = activePivot.matrixWorld.clone();

  snapshotSelection(list);
  bindTransformListeners(resolvedControls, list);
  resolvedControls.attach(activePivot);

  return activePivot;
}

export function refreshMultiSelectionTransform({
  scene,
  transformControls,
  objects,
} = {}) {
  const list = (objects || []).filter(Boolean);
  if (list.length <= 1) {
    detachMultiSelectionTransform({ scene, transformControls });
    const resolvedControls = transformControls || transformControlsRef;
    if (list.length === 1) {
      resolvedControls?.attach?.(list[0]);
      return list[0];
    }
    return null;
  }

  if (
    activePivot &&
    (transformControls || transformControlsRef)?.object === activePivot
  ) {
    activePivot.userData.multiSelectionObjects = list;
    if (computePivotPosition(list, _center)) {
      activePivot.position.copy(_center);
      activePivot.updateMatrixWorld(true);
    }
    activePivot.userData.dragStartPivotWorld =
      activePivot.matrixWorld.clone();
    snapshotSelection(list);
    const resolvedControls = transformControls || transformControlsRef;
    bindTransformListeners(resolvedControls, list);
    resolvedControls.attach(activePivot);
    return activePivot;
  }

  return attachMultiSelectionTransform({ scene, transformControls, objects: list });
}

export function detachMultiSelectionTransform({
  scene,
  transformControls,
} = {}) {
  const resolvedScene = scene || sceneRef;
  const resolvedControls = transformControls || transformControlsRef;

  unbindTransformListeners(resolvedControls);

  if (resolvedControls?.object === activePivot) {
    resolvedControls.detach();
  }

  if (resolvedScene) {
    const existing = resolvedScene.getObjectByName(MULTI_SELECTION_PIVOT_NAME);
    if (existing?.parent) {
      existing.parent.remove(existing);
    }
  } else {
    removePivotFromScene();
  }

  activePivot = null;
}
